import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Search, Filter, Snowflake, Flame, Droplets, Wind, 
  Euro, Calendar, User, Phone, Mail, MapPin, Tag, Percent,
  ChevronRight, Star, Wifi, Check, X, RefreshCw, Clock,
  FileText, Trash2, Eye, ShoppingCart, BarChart3, TrendingUp,
  Store as StoreIcon, Users as UsersIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { storeApi, formatApiError } from '../lib/api';
import { CATEGORY_LABELS, CATEGORY_STYLES } from '../components/store/storeConstants';
import { ProductCardAgent } from '../components/store/ProductCardAgent';
import { useAuth } from '../context/AuthContext';
import { StoreQuotesMonitoring } from '../components/store/StoreQuotesMonitoring';

export default function StoreQuotesPage() {
  const navigate = useNavigate();
  const { isAdmin, isStoreManager } = useAuth();
  const canViewAll = isAdmin || isStoreManager;
  const [activeTab, setActiveTab] = useState('catalogo');
  
  // Catalogo state
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: '',
    brand: '',
    search: '',
    has_promo: false
  });
  const [availableFilters, setAvailableFilters] = useState({ brands: [], categories: [], product_lines: [] });
  
  // Quote state
  const [quotes, setQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [deletingQuote, setDeletingQuote] = useState(false);
  const [quoteStatusFilter, setQuoteStatusFilter] = useState('');
  
  // Dialog state
  const [showNewQuoteDialog, setShowNewQuoteDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newCustomer, setNewCustomer] = useState({
    tipo: 'privato',
    nome: '',
    cognome: '',
    telefono: '',
    email: '',
    indirizzo: '',
    citta: '',
    provincia: '',
    cap: '',
    is_existing_customer: false,
    wants_to_become_customer: false
  });
  const [creatingQuote, setCreatingQuote] = useState(false);

  // Load products
  const loadProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const params = { is_active: true, limit: 100 };
      if (filters.category) params.category = filters.category;
      if (filters.brand) params.brand = filters.brand;
      if (filters.search) params.search = filters.search;
      if (filters.has_promo) params.has_promo = true;
      
      const response = await storeApi.getProducts(params);
      setProducts(response.data.items || []);
      setAvailableFilters(response.data.filters || { brands: [], categories: [], product_lines: [] });
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setProductsLoading(false);
    }
  }, [filters]);

  // Load quotes
  const loadQuotes = useCallback(async () => {
    setQuotesLoading(true);
    try {
      const params = { limit: 50 };
      if (quoteStatusFilter) params.status = quoteStatusFilter;
      const response = await storeApi.getQuotes(params);
      setQuotes(response.data.items || []);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setQuotesLoading(false);
    }
  }, [quoteStatusFilter]);

  // Elimina preventivo (admin only)
  const handleConfirmDeleteQuote = async () => {
    if (!quoteToDelete) return;
    setDeletingQuote(true);
    try {
      await storeApi.deleteQuote(quoteToDelete.id);
      toast.success(`Preventivo ${quoteToDelete.quote_number} eliminato`);
      setQuoteToDelete(null);
      // Ottimizza refresh: rimuove localmente + ricarica
      setQuotes((prev) => prev.filter((q) => q.id !== quoteToDelete.id));
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeletingQuote(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    if (activeTab === 'preventivi') {
      loadQuotes();
    }
  }, [activeTab, loadQuotes]);

  // Handle product selection for new quote
  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setShowNewQuoteDialog(true);
  };

  // Create new quote with product
  const handleCreateQuote = async () => {
    if (!newCustomer.nome || !newCustomer.cognome) {
      toast.error('Inserisci nome e cognome del cliente');
      return;
    }
    
    setCreatingQuote(true);
    try {
      // 1. Create the quote
      const quoteResponse = await storeApi.createQuote({
        customer: newCustomer,
        valid_days: 30
      });
      
      const quoteId = quoteResponse.data.id;
      
      // 2. Add the selected product
      if (selectedProduct) {
        await storeApi.addProductToQuote(quoteId, {
          product_id: selectedProduct.id,
          quantity: 1,
          selected_thermostat: false,
          selected_promos: []
        });
      }
      
      toast.success('Preventivo creato con successo!');
      setShowNewQuoteDialog(false);
      setSelectedProduct(null);
      setNewCustomer({
        tipo: 'privato',
        nome: '',
        cognome: '',
        telefono: '',
        email: '',
        indirizzo: '',
        citta: '',
        provincia: '',
        cap: '',
        is_existing_customer: false,
        wants_to_become_customer: false
      });
      
      // Navigate to quote detail
      navigate(`/store/quotes/${quoteId}`);
      
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setCreatingQuote(false);
    }
  };

  // Quote Row Component
  const QuoteRow = ({ quote }) => {
    const statusColors = {
      'draft': 'bg-gray-100 text-gray-700',
      'sent': 'bg-blue-100 text-blue-700',
      'accepted': 'bg-green-100 text-green-700',
      'rejected': 'bg-red-100 text-red-700',
      'expired': 'bg-orange-100 text-orange-700',
      'converted': 'bg-purple-100 text-purple-700'
    };
    
    const statusLabels = {
      'draft': 'Bozza',
      'sent': 'Consegnato',
      'accepted': 'Accettato',
      'rejected': 'Rifiutato',
      'expired': 'Scaduto',
      'converted': 'Convertito'
    };
    
    return (
      <Card 
        className="hover:shadow-md transition-shadow cursor-pointer"
        onClick={() => navigate(`/store/quotes/${quote.id}`)}
        data-testid={`quote-row-${quote.quote_number}`}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold truncate">{quote.quote_number}</p>
                <p className="text-sm text-muted-foreground truncate">
                  {quote.customer?.nome} {quote.customer?.cognome}
                </p>
              </div>
            </div>

            {/* Store + Agente (admin e responsabile store) */}
            {canViewAll && (
              <div className="hidden md:flex flex-col items-start gap-0.5 min-w-[140px] max-w-[180px]">
                <div className="flex items-center gap-1.5 text-xs text-slate-600 truncate w-full" title={quote.store_id || ''}>
                  <StoreIcon className="h-3 w-3 text-slate-400 flex-shrink-0" />
                  <span className="truncate">{quote.store_id || '—'}</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-600 truncate w-full" title={quote.agent_name || ''}>
                  <UsersIcon className="h-3 w-3 text-slate-400 flex-shrink-0" />
                  <span className="truncate">{quote.agent_name || '—'}</span>
                </div>
              </div>
            )}
            
            <div className="flex items-center gap-4 flex-shrink-0">
              <div className="text-right">
                <p className="font-bold text-lg">
                  € {quote.total_gross?.toLocaleString('it-IT', { minimumFractionDigits: 2 }) || '0,00'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {new Date(quote.created_at).toLocaleDateString('it-IT')}
                </p>
              </div>
              
              <Badge className={statusColors[quote.status] || 'bg-gray-100'}>
                {statusLabels[quote.status] || quote.status}
              </Badge>
              
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    setQuoteToDelete(quote);
                  }}
                  data-testid={`delete-quote-btn-${quote.quote_number}`}
                  title="Elimina preventivo (solo admin)"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}

              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Store HVAC</h1>
          <p className="text-muted-foreground mt-1">
            Configuratore rapido per climatizzatori, caldaie e scaldacqua
          </p>
        </div>
        
        <Button 
          onClick={() => {
            setSelectedProduct(null);
            setShowNewQuoteDialog(true);
          }}
          className="gap-2"
          data-testid="new-quote-btn"
        >
          <Plus className="h-4 w-4" />
          Nuovo Preventivo
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full max-w-2xl grid-cols-3">
          <TabsTrigger value="catalogo" className="gap-2" data-testid="tab-catalogo">
            <ShoppingCart className="h-4 w-4" />
            Catalogo
          </TabsTrigger>
          <TabsTrigger value="preventivi" className="gap-2" data-testid="tab-preventivi">
            <FileText className="h-4 w-4" />
            {canViewAll ? 'Preventivi' : 'I miei preventivi'} ({quotes.length})
          </TabsTrigger>
          <TabsTrigger value="monitoraggio" className="gap-2" data-testid="tab-monitoraggio">
            <BarChart3 className="h-4 w-4" />
            {canViewAll ? 'Monitoraggio' : 'I miei numeri'}
          </TabsTrigger>
        </TabsList>

        {/* Catalogo Tab */}
        <TabsContent value="catalogo" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs text-muted-foreground">Cerca</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input 
                      placeholder="Cerca modello, marca..."
                      value={filters.search}
                      onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                      className="pl-10"
                      data-testid="search-input"
                    />
                  </div>
                </div>
                
                <div className="w-[180px]">
                  <Label className="text-xs text-muted-foreground">Categoria</Label>
                  <Select 
                    value={filters.category} 
                    onValueChange={(v) => setFilters(f => ({ ...f, category: v === 'all' ? '' : v }))}
                  >
                    <SelectTrigger data-testid="filter-category">
                      <SelectValue placeholder="Tutte" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte</SelectItem>
                      {availableFilters.categories.map(cat => (
                        <SelectItem key={cat} value={cat}>
                          {CATEGORY_LABELS[cat] || cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="w-[150px]">
                  <Label className="text-xs text-muted-foreground">Marca</Label>
                  <Select 
                    value={filters.brand} 
                    onValueChange={(v) => setFilters(f => ({ ...f, brand: v === 'all' ? '' : v }))}
                  >
                    <SelectTrigger data-testid="filter-brand">
                      <SelectValue placeholder="Tutte" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte</SelectItem>
                      {availableFilters.brands.map(brand => (
                        <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  variant={filters.has_promo ? 'default' : 'outline'}
                  onClick={() => setFilters(f => ({ ...f, has_promo: !f.has_promo }))}
                  className="gap-2"
                  data-testid="filter-promo"
                >
                  <Tag className="h-4 w-4" />
                  Con Promo
                </Button>
                
                <Button 
                  variant="ghost" 
                  onClick={() => setFilters({ category: '', brand: '', search: '', has_promo: false })}
                  className="gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Reset
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Products Grid */}
          {productsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <Card key={`skeleton-prod-${i}`} className="animate-pulse">
                  <CardHeader className="space-y-2">
                    <div className="h-10 w-10 bg-gray-200 rounded-lg" />
                    <div className="h-4 w-1/3 bg-gray-200 rounded" />
                    <div className="h-5 w-2/3 bg-gray-200 rounded" />
                  </CardHeader>
                  <CardContent>
                    <div className="h-8 w-1/2 bg-gray-200 rounded" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : products.length === 0 ? (
            <Card className="p-8 text-center">
              <Snowflake className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium">Nessun prodotto trovato</p>
              <p className="text-muted-foreground">Prova a modificare i filtri di ricerca</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {products.map(product => (
                <ProductCardAgent
                  key={product.id}
                  product={product}
                  onSelect={() => handleSelectProduct(product)}
                  buttonLabel="Crea Preventivo"
                />
              ))}
            </div>
          )}
          
          <p className="text-sm text-muted-foreground text-center">
            {products.length} prodotti trovati
          </p>
        </TabsContent>

        {/* Preventivi Tab */}
        <TabsContent value="preventivi" className="space-y-4">
          {/* Filtro per stato */}
          <div className="flex flex-wrap gap-2" data-testid="quote-status-filter">
            {[
              { value: '', label: 'Tutti' },
              { value: 'draft', label: 'Bozza' },
              { value: 'sent', label: 'Consegnato' },
              { value: 'accepted', label: 'Accettato' },
              { value: 'converted', label: 'Convertito' },
              { value: 'rejected', label: 'Rifiutato' },
              { value: 'expired', label: 'Scaduto' },
            ].map((opt) => (
              <Button
                key={opt.value || 'all'}
                size="sm"
                variant={quoteStatusFilter === opt.value ? 'default' : 'outline'}
                onClick={() => setQuoteStatusFilter(opt.value)}
                data-testid={`quote-status-filter-${opt.value || 'all'}`}
              >
                {opt.label}
              </Button>
            ))}
          </div>

          {quotesLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Card key={`skeleton-quote-${i}`} className="animate-pulse p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 bg-gray-200 rounded-lg" />
                      <div className="space-y-2">
                        <div className="h-4 w-32 bg-gray-200 rounded" />
                        <div className="h-3 w-24 bg-gray-200 rounded" />
                      </div>
                    </div>
                    <div className="h-6 w-20 bg-gray-200 rounded" />
                  </div>
                </Card>
              ))}
            </div>
          ) : quotes.length === 0 ? (
            <Card className="p-8 text-center">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium">Nessun preventivo</p>
              <p className="text-muted-foreground mb-4">
                Seleziona un prodotto dal catalogo per creare il primo preventivo
              </p>
              <Button onClick={() => setActiveTab('catalogo')}>
                Vai al Catalogo
              </Button>
            </Card>
          ) : (
            <div className="space-y-3">
              {quotes.map(quote => (
                <QuoteRow key={quote.id} quote={quote} />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Monitoraggio Tab */}
        <TabsContent value="monitoraggio" className="space-y-4" data-testid="monitoraggio-content">
          <StoreQuotesMonitoring isAdmin={canViewAll} active={activeTab === 'monitoraggio'} />
        </TabsContent>
      </Tabs>

      {/* New Quote Dialog */}
      <Dialog open={showNewQuoteDialog} onOpenChange={setShowNewQuoteDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo Preventivo Store</DialogTitle>
            <DialogDescription>
              {selectedProduct 
                ? `Crea preventivo per ${selectedProduct.brand} ${selectedProduct.model}`
                : 'Inserisci i dati del cliente per creare un nuovo preventivo'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedProduct && (
            <div className="bg-blue-50 p-3 rounded-lg flex items-center gap-3 mb-4">
              <div className="p-2 bg-white rounded">
                {React.createElement((CATEGORY_STYLES[selectedProduct.category] || {}).icon || Tag, { className: "h-5 w-5 text-blue-600" })}
              </div>
              <div className="flex-1">
                <p className="font-medium">{selectedProduct.brand} {selectedProduct.product_line} {selectedProduct.model}</p>
                <p className="text-sm text-muted-foreground">€ {(selectedProduct.customer_price || selectedProduct.promo_price || selectedProduct.base_price)?.toLocaleString('it-IT')}</p>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nome *</Label>
                <Input 
                  value={newCustomer.nome}
                  onChange={(e) => setNewCustomer(c => ({ ...c, nome: e.target.value }))}
                  placeholder="Mario"
                  data-testid="customer-nome"
                />
              </div>
              <div>
                <Label>Cognome *</Label>
                <Input 
                  value={newCustomer.cognome}
                  onChange={(e) => setNewCustomer(c => ({ ...c, cognome: e.target.value }))}
                  placeholder="Rossi"
                  data-testid="customer-cognome"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Telefono</Label>
                <Input 
                  value={newCustomer.telefono}
                  onChange={(e) => setNewCustomer(c => ({ ...c, telefono: e.target.value }))}
                  placeholder="+39 333 1234567"
                  data-testid="customer-telefono"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer(c => ({ ...c, email: e.target.value }))}
                  placeholder="mario.rossi@email.it"
                  data-testid="customer-email"
                />
              </div>
            </div>
            
            <div>
              <Label>Indirizzo installazione</Label>
              <Input 
                value={newCustomer.indirizzo}
                onChange={(e) => setNewCustomer(c => ({ ...c, indirizzo: e.target.value }))}
                placeholder="Via Roma 1"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label>Città</Label>
                <Input 
                  value={newCustomer.citta}
                  onChange={(e) => setNewCustomer(c => ({ ...c, citta: e.target.value }))}
                  placeholder="Milano"
                />
              </div>
              <div>
                <Label>Prov.</Label>
                <Input 
                  value={newCustomer.provincia}
                  onChange={(e) => setNewCustomer(c => ({ ...c, provincia: e.target.value.toUpperCase().slice(0, 2) }))}
                  placeholder="MI"
                  maxLength={2}
                />
              </div>
            </div>
            
            <div className="flex items-center gap-4 pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox"
                  checked={newCustomer.is_existing_customer}
                  onChange={(e) => setNewCustomer(c => ({ ...c, is_existing_customer: e.target.checked }))}
                  className="rounded"
                />
                <span className="text-sm">Già cliente luce/gas</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox"
                  checked={newCustomer.wants_to_become_customer}
                  onChange={(e) => setNewCustomer(c => ({ ...c, wants_to_become_customer: e.target.checked }))}
                  className="rounded"
                />
                <span className="text-sm">Vuole diventare cliente</span>
              </label>
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowNewQuoteDialog(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleCreateQuote}
              disabled={creatingQuote || !newCustomer.nome || !newCustomer.cognome}
              data-testid="create-quote-btn"
            >
              {creatingQuote ? 'Creazione...' : 'Crea Preventivo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AlertDialog Elimina preventivo (admin only) */}
      <AlertDialog open={!!quoteToDelete} onOpenChange={(o) => !o && !deletingQuote && setQuoteToDelete(null)}>
        <AlertDialogContent data-testid="delete-quote-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" /> Elimina preventivo?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare <strong className="font-mono">{quoteToDelete?.quote_number}</strong>
              {quoteToDelete?.customer?.cognome && (
                <> di <strong>{quoteToDelete.customer.nome} {quoteToDelete.customer.cognome}</strong></>
              )}.
              <br /><br />
              L&apos;operazione è <strong className="text-red-700">irreversibile</strong>: verranno rimossi
              il preventivo, tutte le sue righe e le versioni PDF collegate. Continuare?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingQuote}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteQuote}
              disabled={deletingQuote}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-quote-btn"
            >
              {deletingQuote ? 'Eliminazione…' : 'Elimina definitivamente'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
